import * as React from "react"

import { cn } from "@/lib/utils"
import { Card, CardContent } from "@/components/ui/card"
import { LoadError, type LoadErrorProps } from "@/components/ui/load-error"

/**
 * The one empty state.
 *
 * WHY THIS EXISTS
 * There were 21 hand-rolled "nothing here" blocks and they disagreed about
 * everything, including how much of an answer to give:
 *
 *   Campaigns  a Card, py-12, a 48px icon, `h3 text-lg font-semibold mb-2`,
 *              and a sentence of guidance
 *   Tasks      `<div className="text-center py-8 text-muted-foreground">No
 *              tasks found</div>` — one line, no icon, no next step
 *   Contacts   title, guidance, AND two buttons
 *
 * Same moment in the product, three different levels of helpfulness. The
 * version that only says "No tasks found" is the real cost: it tells the reader
 * the query returned nothing and leaves them to guess whether that is because
 * they have no tasks, or because a filter is on.
 *
 * So `title` and `description` are both part of the contract, and `actions` is
 * there because an empty state is usually the best possible moment to offer the
 * thing that would fill it.
 *
 * HEADING LEVEL (A26). The title is an <h2> by default: an empty state usually
 * stands in for the page's own content, directly under its <h1>, and the old
 * fixed <h3> skipped a level there (axe `heading-order`). Inside a card that
 * already has its own heading, pass `headingLevel={3}`.
 *
 * TYPE (A26). Rungs only — `text-body-lg` / `text-body` / `text-label` — not
 * the stock `text-base` / `text-sm` it was written with before the PropPay
 * scale existed.
 *
 * `asCard` exists because roughly half these blocks already sit inside a
 * CardContent. Nesting a Card in a Card double-draws the border and the
 * gradient, which is why some of them looked heavier than others.
 */
export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Lucide icon. Sized and coloured here so call sites cannot drift. */
  icon?: React.ElementType<{ className?: string }>
  /** What is empty. Sentence case, no trailing period. */
  title: React.ReactNode
  /**
   * Why it is empty and what to do next — the half that was usually missing.
   * If a filter could be the cause, say so.
   */
  description?: React.ReactNode
  /** The action that would fill it. */
  actions?: React.ReactNode
  /** Wrap in a Card. Omit when already inside a CardContent. */
  asCard?: boolean
  /** `compact` for an empty state inside a widget or a table body. */
  size?: "default" | "compact"
  /** Heading level of the title — h2 by default, see HEADING LEVEL above. */
  headingLevel?: 2 | 3 | 4
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  actions,
  asCard,
  size = "default",
  headingLevel = 2,
  className,
  ...props
}: EmptyStateProps) {
  const Heading = `h${headingLevel}` as "h2" | "h3" | "h4"
  const compact = size === "compact"
  const body = (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "gap-2 py-8" : "gap-3 py-12 md:py-16",
        className
      )}
      /* Marker for scripts/fixture-audit.mjs.

         An empty state is CORRECT behaviour and indistinguishable from a bug
         from the outside — which is how the Email Marketing page, the Companies
         list, the Users list and three dashboard widgets each spent months
         rendering nothing while every audit reported them clean. The audit
         cross-references these against what the fixture actually returns, so a
         surface that is empty because its payload was the wrong SHAPE gets
         separated from one that is empty because it has no data. */
      data-empty-state=""
      {...props}
    >
      {Icon ? (
        <div
          aria-hidden="true"
          className={cn(
            "flex items-center justify-center rounded-full bg-foreground/[.05] text-muted-foreground",
            compact ? "size-10" : "size-14"
          )}
        >
          <Icon className={compact ? "size-5" : "size-6"} />
        </div>
      ) : null}
      <div className="space-y-1">
        <Heading
          className={cn(
            "font-semibold text-foreground",
            compact ? "text-body" : "text-body-lg"
          )}
        >
          {title}
        </Heading>
        {description ? (
          // max-w so a long sentence wraps into a readable column rather than
          // stretching the full width of a desktop table. A div, not a <p>: a
          // description may carry its own block content.
          <div
            className={cn(
              "mx-auto max-w-sm text-muted-foreground",
              compact ? "text-label" : "text-body"
            )}
          >
            {description}
          </div>
        ) : null}
      </div>
      {actions ? (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{actions}</div>
      ) : null}
    </div>
  )

  if (!asCard) return body
  return (
    <Card>
      <CardContent className="p-0">{body}</CardContent>
    </Card>
  )
}
EmptyState.displayName = "EmptyState"

/**
 * The one "we could not load this" state — now `LoadError` (ui/load-error.tsx),
 * which this name keeps for the call sites written against it. Same props as
 * before; it gains `role="alert"`, a squared retry button, `retrying` and
 * `error` (read through describeApiError). Unlike LoadError it does NOT wrap
 * itself in a card unless asked (`asCard`), as before.
 */
export type ErrorStateProps = LoadErrorProps

export function ErrorState({ asCard = false, ...props }: ErrorStateProps) {
  return <LoadError asCard={asCard} {...props} />
}
ErrorState.displayName = "ErrorState"

export { LoadError }
export type { LoadErrorProps }
